import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { MapPin, Phone, WhatsappLogo, Envelope } from '@phosphor-icons/react';
import { Button } from './ui/button';

const CountryModal = ({ country, open, onOpenChange }) => {
  const [churches, setChurches] = useState([]);
  const [loading, setLoading]   = useState(false);

  const fetchData = useCallback(async () => {
    if (!country) return;
    setLoading(true);
    const { data } = await supabase
      .from('churches')
      .select('*')
      .eq('country_code', country.flag_code)
      .order('name');
    setChurches(data || []);
    setLoading(false);
  }, [country]);

  useEffect(() => {
    if (open && country) fetchData();
    if (!open) setChurches([]);
  }, [open, country, fetchData]);

  if (!country) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full overflow-hidden shadow-lg border-4 border-white flex-shrink-0">
              <img
                src={`https://flagcdn.com/w160/${country.flag_code.toLowerCase()}.png`}
                srcSet={`https://flagcdn.com/w320/${country.flag_code.toLowerCase()}.png 2x`}
                alt={`Bandera de ${country.name}`}
                className="w-full h-full object-cover"
              />
            </div>
            <DialogTitle className="text-3xl font-black">{country.name}</DialogTitle>
          </div>
        </DialogHeader>

        <div className="mt-6 space-y-6">
          {loading ? (
            <div className="text-center py-8 text-[#737373]">
              <div className="w-6 h-6 border-2 border-[#F04A22] border-t-transparent rounded-full animate-spin mx-auto mb-2" />
              <p>Cargando...</p>
            </div>
          ) : (
            <>
              {churches.length > 0 ? (
                <div>
                  <h3 className="text-xl font-bold mb-4">
                    Iglesias de Contacto en {country.name}
                  </h3>
                  <div className="space-y-4">
                    {churches.map(church => (
                      <div
                        key={church.id}
                        className="bg-[#FAFAFA] border border-[#E5E5E5] rounded-lg p-5 hover:shadow-md transition-shadow"
                      >
                        <h4 className="font-bold text-lg mb-4 text-[#F04A22]">{church.name}</h4>

                        <div className="space-y-3">
                          {/* ── Teléfono con tipo de contacto ── */}
                          <div className="flex items-center gap-3 flex-wrap">
                            {/* Ícono principal según tipo */}
                            {church.phone_is_whatsapp && !church.phone_is_call ? (
                              <WhatsappLogo size={18} weight="fill" className="text-[#25D366] flex-shrink-0" />
                            ) : (
                              <Phone size={18} weight="bold" className="text-[#F04A22] flex-shrink-0" />
                            )}

                            <div className="flex-1 min-w-0">
                              <p className="text-xs text-[#737373]">Teléfono</p>
                              <p className="font-semibold text-sm">{church.phone}</p>
                            </div>

                            {/* Badges y botones de acción */}
                            <div className="flex items-center gap-2 flex-wrap">
                              {church.phone_is_call && (
                                <Button
                                  onClick={() => { window.location.href = `tel:${church.phone}`; }}
                                  size="sm"
                                  className="bg-[#F04A22] hover:bg-[#D03A18] text-white flex items-center gap-1.5"
                                >
                                  <Phone size={14} weight="bold" />
                                  Llamar
                                </Button>
                              )}
                              {church.phone_is_whatsapp && (
                                <Button
                                  onClick={() => {
                                    const num = church.phone.replace(/\D/g, '');
                                    window.open(`https://wa.me/${num}`, '_blank');
                                  }}
                                  size="sm"
                                  className="bg-[#25D366] hover:bg-[#1ebe5d] text-white flex items-center gap-1.5"
                                >
                                  <WhatsappLogo size={14} weight="fill" />
                                  WhatsApp
                                </Button>
                              )}
                            </div>
                          </div>

                          {/* ── Email ── */}
                          {church.email && (
                            <div className="flex items-center gap-3">
                              <Envelope size={18} weight="bold" className="text-[#F04A22] flex-shrink-0" />
                              <div className="flex-1 min-w-0">
                                <p className="text-xs text-[#737373]">Correo electrónico</p>
                                <a
                                  href={`mailto:${church.email}`}
                                  className="font-semibold text-sm hover:text-[#F04A22] transition-colors break-all"
                                >
                                  {church.email}
                                </a>
                              </div>
                              <Button
                                onClick={() => { window.location.href = `mailto:${church.email}`; }}
                                size="sm"
                                variant="outline"
                                className="border-[#F04A22] text-[#F04A22] hover:bg-[#F04A22] hover:text-white flex items-center gap-1.5"
                              >
                                <Envelope size={14} weight="bold" />
                                Escribir
                              </Button>
                            </div>
                          )}

                          {/* ── Dirección ── */}
                          <div className="flex items-start gap-3">
                            <MapPin size={18} weight="bold" className="text-[#F04A22] flex-shrink-0 mt-1" />
                            <div className="flex-1">
                              <p className="text-xs text-[#737373]">Dirección</p>
                              {church.city && (
                                <p className="font-semibold text-sm">{church.city}</p>
                              )}
                              <p className="text-sm text-[#111111]/80">{church.address}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 bg-[#FAFAFA] rounded-lg border border-[#E5E5E5]">
                  <MapPin size={48} className="mx-auto mb-3 text-[#E5E5E5]" weight="light" />
                  <p className="text-[#737373] mb-2">
                    Aún no hay iglesias en {country.name}
                  </p>
                  <p className="text-sm text-[#737373]">
                    Contáctanos para ser agregado.
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CountryModal;
